import {Component, DoCheck, EventEmitter, Input, OnDestroy, Output, ChangeDetectionStrategy} from '@angular/core';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {EmployeeDetailsDialogComponent} from '../employee-details-dialog/employee-details-dialog.component';
import {PayrollService} from '../../../services/payroll/payroll.service';
import {WorkforceBrowseState} from '../../../services/payroll-workforce/workforce-browse.state';
import {WorkforceTeamCreationState} from '../../../services/payroll-workforce/workforce-team-creation.state';
import {EditorEmployee, WorkforceTeamEditorState} from '../../../services/payroll-workforce/workforce-team-editor.state';

// The directory uses the same bounded reads and ordered selection intent as the
// team editor. It never downloads a census or resolves account authority locally.
@Component({selector: 'app-payroll-workforce-employees', standalone: false,
  templateUrl: './payroll-workforce-employees.component.html',
  styleUrls: ['./payroll-workforce-employees.component.scss'], changeDetection: ChangeDetectionStrategy.Eager})
export class PayrollWorkforceEmployeesComponent implements DoCheck, OnDestroy {
  @Input() state!: WorkforceBrowseState;
  @Input() creation!: WorkforceTeamCreationState;
  @Input() active = false;
  @Output() createTeam = new EventEmitter<WorkforceTeamEditorState>();
  @Input() draft: {editor: WorkforceTeamEditorState | null} = {editor: null};
  get editor(): WorkforceTeamEditorState | null { return this.draft.editor; }
  set editor(editor: WorkforceTeamEditorState | null) { this.draft.editor = editor; }
  private source = '';
  private disposed = false;
  refreshing = false;
  readonly columns = [['employmentStatus', 'Status'], ['jobTitle', 'Job title'], ['location', 'Location'],
    ['division', 'Division'], ['department', 'Department'], ['manager', 'Manager'], ['payGroup', 'Pay type'],
    ['workerType', 'Employee type'], ['state', 'State']];
  private detailsRef?: MatDialogRef<EmployeeDetailsDialogComponent>;
  constructor(private api: PayrollService, private dialog: MatDialog) {}
  get busy(): boolean {
    const progress = this.creation?.progress;
    return !!this.creation?.busy || !!progress && progress.execution !== 'background' && !['complete', 'closed'].includes(progress.phase);
  }
  get accessPending(): boolean { return this.state.mode !== 'snapshot' || this.refreshing; }
  get canCreate(): boolean { return !this.accessPending && !this.busy && !!this.editor?.ready && this.state.mode === 'snapshot'
    && this.editor.snapshotId === this.state.context?.snapshot?.id && !this.editor.counting && !!this.editor.counts?.selected; }
  ngDoCheck(): void { this.syncSource(); }
  private syncSource(): void {
    if (this.disposed) return;
    const context = this.state?.context;
    const available = this.state?.mode === 'snapshot' && context?.availability === 'ready'
      && context.scope.companyId === this.state.companyId && context.scope.providerSlug === 'ukg_ready'
      && context.scope.providerSlot === 'primary' && !!context.snapshot?.id;
    const source = available ? JSON.stringify([this.state.companyId, context!.snapshot!.id]) : '';
    if (!available) this.closeDetails();
    if (!source && this.editor?.companyId === this.state.companyId
      && (this.state.transientFailure || this.state.mode === 'resolving')) return;
    if (!source) { this.editor?.destroy(); this.editor = null; this.source = ''; return; }
    if (!this.source && source && this.editor?.snapshotId === context?.snapshot?.id && this.editor?.companyId === this.state.companyId) this.source = source;
    if (source === this.source || !this.active && !this.editor && source) return;
    this.closeDetails();
    const previous = this.editor;
    const sameCompany = previous?.companyId === this.state.companyId;
    const criteria = previous?.criteria, search = previous?.search;
    previous?.destroy(); this.editor = null; this.source = source;
    if (!source) return;
    const editor = this.editor = new WorkforceTeamEditorState(this.api, this.state.companyId, undefined, true);
    if (sameCompany && criteria) {
      editor.criteria = criteria; editor.search = search || '';
      editor.notice = 'Employee data changed. Review and select employees again. Your filters are retained.';
    }
    void editor.open(context);
  }
  async refresh(): Promise<void> {
    if (this.refreshing || this.busy) return;
    this.refreshing = true;
    const previous = this.editor;
    try {
      await this.state.reload(); this.syncSource();
      if (this.state.mode === 'snapshot' && this.editor === previous) await this.editor?.open(this.state.context);
    } finally { this.refreshing = false; }
  }
  create(): void { if (this.canCreate && this.editor) this.createTeam.emit(this.editor); }
  scroll(event: Event): void {
    const viewport = event.target as HTMLElement, editor = this.editor;
    if (!editor || editor.loading || this.accessPending) return;
    const backwards = viewport.scrollTop === 0 && editor.hasPrevious;
    if (!backwards && (viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight >= 100 || !editor.next)) return;
    // Preserve the visible row's position when the bounded window evicts rows.
    const anchor = Array.from(viewport.querySelectorAll<HTMLElement>('tbody tr')).find(row => row.getBoundingClientRect().bottom > viewport.getBoundingClientRect().top + 60);
    const key = anchor?.dataset['employeeKey'], top = anchor?.getBoundingClientRect().top;
    void editor.more(backwards ? editor.rows[0]?.rowId : undefined).then(() => requestAnimationFrame(() => {
      if (this.disposed || this.editor !== editor || !key || top === undefined) return;
      const nextAnchor = Array.from(viewport.querySelectorAll<HTMLElement>('tbody tr')).find(row => row.dataset['employeeKey'] === key);
      if (nextAnchor) viewport.scrollTop += nextAnchor.getBoundingClientRect().top - top;
    }));
  }
  showDetails(row: EditorEmployee): void {
    if (this.disposed || this.accessPending || !this.editor?.rows.includes(row)
      || this.editor.companyId !== this.state.companyId || this.editor.snapshotId !== this.state.context?.snapshot?.id) return;
    this.closeDetails();
    // Use only the safe snapshot projection already loaded for this row. The
    // legacy dialog remains read-only; no live census or provider lookup occurs.
    this.detailsRef = this.dialog.open(EmployeeDetailsDialogComponent, {
      width: '820px', maxWidth: '95vw', autoFocus: false, panelClass: 'employee-details-dialog-panel',
      data: {employee: {...row}, allowEditing: false},
    });
  }
  private closeDetails(): void { this.detailsRef?.close(); this.detailsRef = undefined; }
  display(field: string, value: any): string {
    let text = String(value || '').trim();
    if (['location', 'department', 'division'].includes(field)) text = text.replace(new RegExp('^' + field + '\\s*\\/\\s*', 'i'), '');
    return text || '—';
  }
  name(row: EditorEmployee): string { return `${row.firstName || ''} ${row.lastName || ''}`.trim() || row.primaryEmail || '—'; }
  reason(code: string | null): string {
    return ({primary_email_required: 'Email address missing. This employee cannot be added yet.',
      identity_conflict: 'Account match is ambiguous.', preflight_blocked: 'Account match could not be verified.',
      projection_unavailable: 'Employee information is incomplete.', employee_ineligible: 'Not eligible for selection.',
      member_previously_removed: 'Previously removed; re-adding is unavailable.', member_protected: 'Protected member.'} as Record<string, string>)[code || ''] || 'Unavailable for selection.';
  }
  // The page owns the draft so leaving this tab preserves selection. Scope
  // changes and page destruction dispose it in PayrollTeamsComponent.
  ngOnDestroy(): void { this.disposed = true; this.closeDetails(); }
}
